/**
 * Message Input Component
 * Mobile-first text input with send button and file attachments
 */

import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import VoiceInput from './VoiceInput';
import ImageUpload from './ImageUpload';

const MessageInput = ({ onSend, disabled = false, isSending = false }) => {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [isFocused, setIsFocused] = useState(false);
  const textareaRef = useRef(null);
  const inputContainerRef = useRef(null);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const newHeight = Math.min(textareaRef.current.scrollHeight, 120);
      textareaRef.current.style.height = newHeight + 'px';
    }
  }, [message]);

  // Scroll input into view when focused (mobile keyboard handling)
  useEffect(() => {
    if (isFocused && inputContainerRef.current) {
      // Small delay to let keyboard appear
      const timeoutId = setTimeout(() => {
        inputContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [isFocused]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!message.trim() && attachments.length === 0) return;
    
    onSend(message.trim(), attachments);
    setMessage('');
    setAttachments([]);
    
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    
    // Process files (for now, just store them)
    const newAttachments = files.map(file => ({
      type: file.type.startsWith('image/') ? 'image' : 'file',
      name: file.name,
      size: file.size,
      file
    }));
    
    setAttachments(prev => [...prev, ...newAttachments]);
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // Handle voice transcript
  const handleVoiceTranscript = (transcript) => {
    setMessage(prev => prev + (prev ? ' ' : '') + transcript);
  };

  // Handle image upload
  const handleImageUpload = async (imageData) => {
    // Add image as attachment
    const imageAttachment = {
      type: 'image',
      name: 'uploaded-image.png',
      data: imageData,
      file: null
    };
    
    setAttachments(prev => [...prev, imageAttachment]);
  };

  return (
    <div className="ai-tutor-input" ref={inputContainerRef}>
      {/* Attachments Preview */}
      {attachments.length > 0 && (
        <div className="ai-tutor-attachments-preview">
          {attachments.map((attachment, index) => (
            <div key={index} className="ai-tutor-attachment-preview">
              <span>{attachment.name}</span>
              <button 
                onClick={() => removeAttachment(index)}
                className="ai-tutor-attachment-remove"
                type="button"
                aria-label="Remove attachment"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Input Form */}
      <form onSubmit={handleSubmit} className="ai-tutor-input-wrapper">
        <div className="ai-tutor-input-field">
          <textarea
            ref={textareaRef}
            className="ai-tutor-textarea"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder={t('aiTutoring.messagePlaceholder')}
            disabled={disabled || isSending}
            aria-busy={isSending}
            rows={1}
            autoComplete="off"
            autoCorrect="on"
            spellCheck="true"
            enterKeyHint="send"
          />
          
          {/* Inline Action Buttons - Inside input field */}
          <div className="ai-tutor-input-actions">
            {/* Voice Input */}
            <VoiceInput 
              onTranscript={handleVoiceTranscript}
              disabled={disabled}
            />

            {/* Image Upload */}
            <ImageUpload
              onImageUpload={handleImageUpload}
              disabled={disabled}
            />

            {/* File Upload (Hidden) */}
            <input
              type="file"
              id="ai-tutor-file-upload"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
              multiple
              accept="image/*,.pdf,.doc,.docx,.txt"
            />

            {/* Send Button */}
            <button
              type="submit"
              className="ai-tutor-send-btn"
              disabled={disabled || isSending || (!message.trim() && attachments.length === 0)}
              title={t('aiTutoring.send')}
              aria-label={t('aiTutoring.send')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path 
                  d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13" 
                  stroke="currentColor" 
                  strokeWidth="2.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default MessageInput;
